import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, avatarOptions, ensureProjectColor } from '../avatar/avatar';
import styles from './ClientDropdownWithProjects.module.scss';
import { routesPath } from '../../constants/routesPath';


export function ClientDropdownWithProjects({ client, colors, projects }) {
  const projectsWithColor = (projects && projects.length ? projects : (client.projects || [])).map(function(p) {
    const colorHex = (colors && p.colour_id) ? (() => {
      const found = colors.find(function(col) { return Number(col.id) === Number(p.colour_id); });
      return found && found.hex;
    })() : undefined;
    return {
      ...p,
      color: colorHex,
      colour: { hex: colorHex }
    };
  });
  const [showProjects, setShowProjects] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setShowProjects(true);
  };
  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => setShowProjects(false), 100);
  };
  let projectsBoxTop = 0;
  let projectsBoxLeft = 0;
  if (showProjects && wrapperRef.current) {
    const rect = wrapperRef.current.getBoundingClientRect();
    projectsBoxTop = rect.top;
    projectsBoxLeft = rect.right + 12;
  }

  // Helper for project count (avoids duplication)
  const projectCount = (projects && projects.length) || (client.projects && client.projects.length) || 0;

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ position: 'relative', marginBottom: '0' }}
    >
      <div
        className={styles.clientDropdownItem}
        ref={wrapperRef}
      >
        <div
          className={styles.clientInfo}
          onClick={() => navigate(routesPath.getClientProjects(client.id))}
          tabIndex={0}
          style={{
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '0',
            boxShadow: 'none',
            padding: '8px 12px',
            width: '100%',
            marginBottom: '0',
            cursor: 'pointer',
            minHeight: '44px',
            transition: 'none',
          }}
        >
          <Avatar {...avatarOptions(client, 'client')} size={28} />
          <div style={{marginLeft: 10, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
            <div style={{fontSize: '17px', fontWeight: 700, color: '#232c3d', marginBottom: 1, display: 'flex', alignItems: 'center'}}>
              {client.title}
              {projectCount > 0 && (
                <span style={{
                  background: '#f3f3f3',
                  color: '#666',
                  fontSize: '12px',
                  fontWeight: 600,
                  marginLeft: 14,
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  width: 18,
                  height: 18,
                  lineHeight: '18px',
                  textAlign: 'center',
                  borderRadius: 0,
                  boxShadow: 'none',
                  border: 'none',
                  padding: 0,
                  boxSizing: 'border-box',
                }} title="Number of projects">
                  {projectCount}
                </span>
              )}
            </div>
            {client.company && (
              <div style={{fontSize: '15px', color: '#b5bdc2', fontWeight: 500}}>{client.company}</div>
            )}
          </div>
          <span
            className={styles.arrowContainer}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              marginLeft: 4,
              color: '#b5bdc2',
              transition: 'color 0.2s',
            }}
          >
            {/* Slightly larger arrow design */}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 7L9 7M9 7L6 4M9 7L6 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div
              style={{
                width: '1px',
                height: '8px',
                backgroundColor: 'currentColor',
                marginLeft: '0',
              }}
            ></div>
          </span>
        </div>
        {showProjects && client.projects && client.projects.length > 0 && (
          <div
            className={styles.projectsDropdownBox}
            style={{
              position: 'fixed',
              left: projectsBoxLeft + 2,
              top: projectsBoxTop,
              boxShadow: '0 2px 12px rgba(0,0,0,0.14)',
              borderRadius: 8,
              padding: '0',
              background: '#fff',
              minWidth: 340,
              zIndex: 1000,
              height: 'auto',
              maxHeight: 'none',
              overflow: 'visible',
            }}
          >
            {/* Heading above projects list */}
            <div style={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#b5bdc2',
              padding: '16px 24px 8px 24px',
              background: '#f8f9fa',
              borderBottom: '1px solid #f0f0f0',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              marginBottom: 0
            }}>SELECT PROJECT</div>
            <ul className={styles.projectsList} style={{padding: 0, margin: 0}}>
              {projectsWithColor.map(function(project) {
                const projectWithColor = ensureProjectColor(project);
                return (
                  <li
                    key={project.id}
                    tabIndex={0}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      position: 'relative',
                    }}
                    onClick={() => navigate(routesPath.projectPipeline(project.id, project.pipeline_id))}
                  >
                    {/* Boxed card for selected project */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#fff',
                      border: '1px solid #e0e0e0',
                      borderRadius: '0',
                      boxShadow: 'none',
                      padding: '8px 12px',
                      width: '100%',
                    }}>
                      <Avatar
                        {...avatarOptions(projectWithColor, 'project')}
                        size={24}
                        round={true}
                        color={projectWithColor.colour && projectWithColor.colour.hex}
                        textSizeRatio={2}
                      />
                      <div style={{marginLeft: 8, flex: 1}}>
                        <div style={{fontSize: '17px', fontWeight: 700, color: '#232c3d', marginBottom: 1}}>{project.title}</div>
                        {project.client && (
                            <div style={{fontSize: '15px', color: '#b5bdc2', fontWeight: 500}}>{project.client.title}</div>
                          )}
                      </div>
                      {/* Removed arrow by projects */}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
